import { type HTMLAttributes } from "react"
import { twMerge } from "tailwind-merge"

const KeyboardShortcutGroup = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={twMerge("flex flex-row gap-0.5", className)} {...props} />
  )
}

export type KeyboardShortcutProps = HTMLAttributes<HTMLDivElement>

/**
 * A component for displaying keyboard shortcuts. Also includes exported symbols for common keyboard keys.
 *
 * ```tsx
 * import { KeyboardShortcut } from "@tokenterminal/ui/keyboard/KeyboardShortcut"
 *
 * <KeyboardShortcut>
 *   {KeyboardShortcut.CommandSymbol}
 * </KeyboardShortcut>
 * <KeyboardShortcut>C</KeyboardShortcut>
 * ```
 */

export const KeyboardShortcut = ({
  className,
  ...props
}: KeyboardShortcutProps) => {
  return (
    <div
      role="img"
      aria-label="Keyboard shortcut"
      className={twMerge(
        "inline-flex items-center justify-center my-0.5 rounded text-[10px] h-[1.6em] min-w-[1.6em] px-[3px] text-fg-default bg-bg-surface border-[0.5px] border-solid border-neutral-900/10 dark:border-neutral-100/10 shadow-[0_1px_0_rgba(0,0,0,0.05)]",
        className
      )}
      {...props}
    />
  )
}

KeyboardShortcut.Group = KeyboardShortcutGroup
KeyboardShortcut.CommandSymbol = "⌘"
KeyboardShortcut.ControlSymbol = "Ctrl"
KeyboardShortcut.OptionSymbol = "⌥"
KeyboardShortcut.ShiftSymbol = "⇧"
